import React from 'react'
import { Controller, Control, FieldErrors } from 'react-hook-form'
import BaseQuestion from '../../base-question'
import SurveyWeightPicker from './weight-picker'

interface WeightQuestionProps {
  question: {
    id: number | string
    text: string
    isRequired?: boolean
    type: string
    placeholder?: string
  }
  // RHF mode
  control?: Control<any>
  errors?: FieldErrors<any>
  // Zustand mode
  value?: string | number
  error?: string
  onChange?: (value: string) => void
}

const WeightQuestion: React.FC<WeightQuestionProps> = ({
  question,
  control,
  errors,
  value,
  error,
  onChange,
}) => {
  const isRHFMode = !!control && !!errors
  const questionId =
    typeof question.id === 'string' ? parseInt(question.id, 10) : question.id

  if (isRHFMode) {
    // RHF mode - use Controller
    return (
      <BaseQuestion
        question={{
          ...question,
          id: questionId,
        }}
        control={control}
        errors={errors}>
        <Controller
          name={`question_${question.id}`}
          control={control}
          rules={{
            required: question.isRequired ? 'This field is required' : false,
            validate: value => {
              if (!question.isRequired && (!value || value === 0)) {
                return true // Optional field can be empty
              }
              if (!value || value === 0) {
                return 'Please enter a valid weight'
              }
              const numValue = parseInt(String(value), 10)
              if (isNaN(numValue) || numValue <= 0) {
                return 'Please enter a valid positive number'
              }
              if (numValue > 1000) {
                return 'Please enter a weight less than 1000 lbs'
              }
              return true
            },
          }}
          render={({ field }) => {
            // Parse the stored value (weight in pounds as string or number)
            const weightValue =
              field.value !== undefined && field.value !== null
                ? parseInt(String(field.value), 10)
                : undefined

            return (
              <SurveyWeightPicker
                value={weightValue}
                onChange={(weight: number) => {
                  // Store as string (matching existing format)
                  field.onChange(String(weight))
                }}
                onBlur={(weight: number) => {
                  // Final validation and storage on blur
                  field.onChange(String(weight))
                }}
                placeholder={question.placeholder || 'Weight'}
              />
            )
          }}
        />
      </BaseQuestion>
    )
  } else {
    // Zustand mode - use direct props
    const parsed = parseInt(String(value), 10)
    const weightValue = parsed > 0 ? parsed : undefined

    return (
      <BaseQuestion
        question={{
          ...question,
          id: questionId,
        }}
        error={error}>
        <SurveyWeightPicker
          value={weightValue}
          onChange={(weight: number) => {
            onChange?.(weight > 0 ? String(weight) : '')
          }}
          onBlur={(weight: number) => {
            onChange?.(weight > 0 ? String(weight) : '')
          }}
          placeholder={question.placeholder || 'Weight'}
        />
      </BaseQuestion>
    )
  }
}

export default WeightQuestion
